import type { CSSProperties } from 'react'

import type { IssueKind } from '../utils/eligibility'

/** Brand green used across the Scheduling pages. */
export const BRAND = '#169b62'

/** Soft fill + dark ink pairs for job chips, picked per category name (no legend). */
const CATEGORY_TONES = [
  { bg: '#CFDDF8', fg: '#16306B' },
  { bg: '#F6D99B', fg: '#5E3C00' },
  { bg: '#F2C3D1', fg: '#7A1633' },
  { bg: '#BFE6E0', fg: '#0F4A44' },
  { bg: '#F8D2B8', fg: '#6B2E0A' },
  { bg: '#D6E4C3', fg: '#2E4210' },
] as const

const NO_CATEGORY_TONE = { bg: '#E4E9E3', fg: '#2F3E36' }

/** Completed jobs: muted grey-green (with a check icon) instead of the category colour. */
export const COMPLETED_TONE = { bg: '#E3EBE5', fg: '#3C4B43' }

/** "#10412345" (null when the job has no ref). */
export const formatJobRef = (ref: string | null | undefined) => {
  const raw = String(ref || '').trim()
  if (!raw) return null
  return raw.startsWith('#') ? raw : `#${raw}`
}

export function categoryTone(name: string | null | undefined) {
  const key = String(name || '').trim().toLowerCase()
  if (!key) return NO_CATEGORY_TONE
  let hash = 0
  for (let index = 0; index < key.length; index += 1) hash = (hash * 31 + key.charCodeAt(index)) >>> 0
  return CATEGORY_TONES[hash % CATEGORY_TONES.length]
}

export const LEAVE_HATCH: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, #F1EBFB 0, #F1EBFB 6px, #E7DDF7 6px, #E7DDF7 12px)',
}
export const OFF_HATCH: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, #EEF1ED 0, #EEF1ED 6px, #E4E9E3 6px, #E4E9E3 12px)',
}
export const OUTSIDE_SHIFT_HATCH: CSSProperties = {
  background: 'repeating-linear-gradient(135deg, #F1F3F0 0, #F1F3F0 6px, #E8ECE7 6px, #E8ECE7 12px)',
}

/** Badge colours per issue kind (always shown with the label text). */
export const ISSUE_TONE: Record<IssueKind, { bg: string; fg: string; label: string }> = {
  open: { bg: '#FFF4D6', fg: '#5E3C00', label: 'Needs worker' },
  leave: { bg: '#EBE3FA', fg: '#42237A', label: 'On leave' },
  off: { bg: '#E4E9E3', fg: '#2F3E36', label: 'Day off' },
  category: { bg: '#E4E9E3', fg: '#2F3E36', label: 'Wrong category' },
  hours: { bg: '#FDE8C8', fg: '#6B3A00', label: 'Outside shift' },
  clash: { bg: '#FADBD8', fg: '#8A1C12', label: 'Clash' },
}

export const PROBLEM_RED = '#B42318'

export const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || '?'

export const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const parseDay = (day: string) => new Date(`${day}T00:00:00`)

/** { dow: 'Tue', num: 6, long: 'Tuesday, Oct 6' } for a YYYY-MM-DD key (local day). */
export function describeDay(day: string) {
  const date = parseDay(day)
  if (Number.isNaN(date.getTime())) return { dow: day, num: '', long: day, short: day }
  return {
    dow: date.toLocaleDateString('en-US', { weekday: 'short' }),
    num: String(date.getDate()),
    long: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
    short: date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' }),
  }
}
